import React, { useEffect, useRef } from "react";

interface CircleProgressBarProps {
  progress: number;
  bgColor: string;
  progressColor: string;
  type?: "solid" | "segmented";
  segmentCount?: number;
  segmentGap?: number;
}

const CircleProgressBar: React.FC<CircleProgressBarProps> = ({
  progress,
  bgColor,
  progressColor,
  type = "segmented",
  segmentCount = 8,
  segmentGap = 2,
}) => {
  const radius = 115;
  const circumference = 2 * Math.PI * radius;
  const circleRef = useRef<SVGCircleElement>(null);

  // Calculate stroke dash offset (unchanged)
  const strokeDashoffset = Math.max(
    0,
    circumference - (Math.min(Math.max(progress, 0), 100) / 100) * circumference
  );

  // Animation logic (unchanged)
  useEffect(() => {
    if (circleRef.current) {
      circleRef.current.style.transition = progress === 0
        ? "none"
        : "stroke-dashoffset 1s linear";
      circleRef.current.style.strokeDashoffset = `${strokeDashoffset}px`;
    }
  }, [strokeDashoffset, progress]);

  // Calculate segments (unchanged)
  const { segmentLength, gapLength } = (() => {
    const gapAngle = segmentGap;
    const segmentAngle = (360 - segmentCount * gapAngle) / segmentCount;
    return {
      segmentLength: (segmentAngle / 360) * circumference,
      gapLength: (gapAngle / 360) * circumference,
    };
  })();

  return (
    <svg width="230" height="230" viewBox="0 0 230 230">
      {/* Background track */}
      <circle
        cx="115"
        cy="115"
        r={radius}
        fill="none"
        stroke={bgColor}
        strokeWidth="17"
        strokeDasharray={type === "segmented" ? `${segmentLength} ${gapLength}` : undefined}
      />

      {/* Mask definition (CRITICAL FIX) */}
      {type === "segmented" && (<>
        <defs>
          <mask id="segment-mask">
            {/* Black background hides everything by default */}
            <rect x="0" y="0" width="230" height="230" fill="black" />
            {/* White segments reveal progress only where needed */}
            <circle
              cx="115"
              cy="115"
              r={radius}
              fill="none"
              stroke="white"
              strokeWidth="17"
              strokeDasharray={`${segmentLength} ${gapLength}`}
              transform="rotate(-90 115 115)"
            />
          </mask>
        </defs>

        <circle
          cx="115"
          cy="115"
          r="92.5" /* 185px diameter / 2 */
          fill="none"
          stroke="#fff"
          strokeWidth="2"
        />
      </>
      )}

      {/* Progress fill (applies mask) */}
      <circle
        ref={circleRef}
        cx="115"
        cy="115"
        r={radius}
        fill="none"
        stroke={progressColor}
        strokeWidth="17"
        strokeDasharray={circumference}
        strokeDashoffset={strokeDashoffset}
        transform="rotate(-90 115 115)"
        mask={type === "segmented" ? "url(#segment-mask)" : undefined}
      />
    </svg>
  );
};

export default CircleProgressBar;